// Pretty-printers for HollowScript's two distinct type representations:
// `TypeNode` (an unresolved *syntactic* type annotation straight from the
// parser, e.g. a ConstantDecl's `: Int` or a Param's declared type - never
// checked against anything) and `Type` (the checker's own resolved type
// value, e.g. a capability's `functionType([...], ...)`). Both are exported
// AST/type shapes (`@hollow/script`'s public index.d.ts), so this stays a
// pure formatting layer with no dependency on the checker's internal
// inference (`checker/types.ts` is not part of the published package - see
// this module's own doc comment in scope.ts for why that boundary matters).
//
// Neither function ever throws: an `ErrorType`/`ErrorExpr`-shaped node is a
// normal, expected input (the source the creator is actively typing is very
// often mid-edit and syntactically incomplete), not a bug.

import type { Type } from '@hollow/script';
import type { TypeNode } from './astTypes.js';

/** Renders a parsed (unchecked) type annotation back to HollowScript syntax,
 * for completion/hover "detail" text on a declaration that carries an
 * explicit `: Type` - the same text a creator already typed, not an
 * inference. */
export function typeNodeToString(node: TypeNode): string {
  switch (node.kind) {
    case 'NamedType': {
      const args = node.typeArguments.length > 0 ? `<${node.typeArguments.map(typeNodeToString).join(', ')}>` : '';
      return node.name + args + (node.optional ? '?' : '');
    }
    case 'ListType':
      return `[${typeNodeToString(node.element)}]${node.optional ? '?' : ''}`;
    case 'FunctionType': {
      const params = node.parameters.map(typeNodeToString).join(', ');
      const ret = node.returnType ? `: ${typeNodeToString(node.returnType)}` : '';
      return `fn(${params})${ret}${node.optional ? '?' : ''}`;
    }
    case 'RecordType': {
      const fields = node.fields.map((f) => `${f.name}: ${typeNodeToString(f.type)}`).join(', ');
      return `{ ${fields} }${node.optional ? '?' : ''}`;
    }
    case 'ErrorType':
      return '?';
  }
}

/** Renders a checker-resolved `Type` (used for the host capability surface -
 * `capabilities.ts`'s `functionType`/`recordType` calls, whose real shapes
 * this reads directly rather than duplicating by hand) back to HollowScript
 * type syntax. */
export function runtimeTypeToString(type: Type): string {
  if (type.kind === 'Optional') return `${runtimeTypeToString(type.base)}?`;
  switch (type.kind) {
    case 'Int':
      return 'Int';
    case 'Float':
      return 'Float';
    case 'Bool':
      return 'Bool';
    case 'String':
      return 'String';
    case 'Filter':
      return 'Filter';
    case 'None':
      return 'None';
    case 'List':
      return `[${runtimeTypeToString(type.element)}]`;
    case 'Record': {
      if (type.name) return type.name;
      const fields = [...type.fields.entries()].map(([name, fieldType]) => `${name}: ${runtimeTypeToString(fieldType)}`);
      return `{ ${fields.join(', ')} }`;
    }
    case 'Function': {
      const params = type.parameters.map(runtimeTypeToString).join(', ');
      const ret = type.returnType ? `: ${runtimeTypeToString(type.returnType)}` : '';
      return `fn(${params})${ret}`;
    }
    case 'TypeParameter':
      return type.name;
    case 'Unresolved':
      return '?';
  }
}

/** Renders a capability function's real `functionType` as a call signature,
 * e.g. `spawn(String, Int, Float, Float, Float): String` - the "detail" text
 * shown next to a completion item and the first line of its hover doc. */
export function functionSignatureText(name: string, type: Type): string {
  const base = type.kind === 'Optional' ? type.base : type;
  if (base.kind !== 'Function') return `${name}: ${runtimeTypeToString(type)}`;
  const params = base.parameters.map(runtimeTypeToString).join(', ');
  const ret = base.returnType ? `: ${runtimeTypeToString(base.returnType)}` : '';
  return `${name}(${params})${ret}`;
}
